/* eslint-disable @typescript-eslint/no-use-before-define */

import { useMemo, useState, useCallback, useRef } from 'react';
import { Node, Edge, addEdge, applyEdgeChanges, applyNodeChanges, useViewport, useReactFlow } from 'reactflow';
import { store } from 'src/redux/store';
import { setNodeConfiguration, setSelectedTrigger } from 'src/redux/workflows/slice';
import { WorkflowItemModel } from 'src/services/workflows';

// Custom hook for managing workflow state and actions
const useWorkflows = (workflowsState: any) => {
  // Initial state for nodes
  const [nodes, setNodes] = useState<Node[]>([
    {
      id: '1-node',
      type: 'trigger',
      position: { x: 100, y: 0 },
      data: { onAddNode, removeLastNode, removeNode, onReplaceNode, scrollToPlusNode, isEditMode: true },
    },
  ]);

  // Destructure viewport properties from useViewport hook
  const { x: viewportX, y: viewportY, zoom } = useViewport();

  const { setCenter } = useReactFlow();

  // Initial state for edges
  const [edges, setEdges] = useState<Edge[]>([]);

  // Refs for latest state — avoids stale closure issues in functions passed to node data
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  // Counter for unique IDs — never decrements, so no duplicates after deletions
  const idCounterRef = useRef(2); // starts at 2 since trigger is node 1

  const getNodeWidth = (nodeType: string) => {
    switch (nodeType) {
      case 'emailBuilder':
        return 640;
      case 'notificationBuilder':
        return 480;
      case 'caseBuilder':
        return 480;
      case 'productBuilder':
        return 480;
      default:
        return 320;
    }
  };

  const NODE_GAP = 68;
  const PLUS_NODE_WIDTH = 32;

  const nodeDataDefaults = () => ({
    onAddNode,
    removeLastNode,
    removeNode,
    onReplaceNode,
    scrollToPlusNode,
    isEditMode: true,
  });

  // Function to add a new node
  function onAddNode(parentId: string, type?: string, actionData?: any) {
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;

    // Check if the node type is 'plus'
    const isPlusType = type === 'plus';

    if (isPlusType) {
      // Adding first plus node after trigger — simple append
      const xPosition = 500;
      const newId = idCounterRef.current++;
      const newNodeId = `${newId}-${type}`;

      const newNode = {
        id: newNodeId,
        type: `${type}`,
        position: { x: xPosition, y: 52.8 },
        data: { label: undefined, ...nodeDataDefaults() },
      };

      setNodes(prevNodes => [...prevNodes, newNode]);
      setEdges(eds =>
        addEdge(
          {
            id: `e${parentId}-${newNodeId}`,
            source: parentId,
            sourceHandle: parentId,
            target: newNodeId,
            targetHandle: parentId,
            type: 'standard',
          },
          eds,
        ),
      );
      return;
    }

    // Adding a real node (condition/action) from a plus node
    const clickedPlusNode = currentNodes.find(n => n.id === parentId);
    if (!clickedPlusNode) return;

    // Find the edge going out from the clicked plus node to determine the next node
    const outgoingEdge = currentEdges.find(e => e.source === parentId);
    const isMiddleInsertion = !!outgoingEdge;

    const newNodeWidth = getNodeWidth(type!);
    const shiftAmount = newNodeWidth + NODE_GAP + PLUS_NODE_WIDTH + NODE_GAP;

    const plusNodeX = clickedPlusNode.position.x;
    const newNodeX = plusNodeX + PLUS_NODE_WIDTH + NODE_GAP;

    // Generate unique IDs
    const nodeId = idCounterRef.current++;
    const plusId = idCounterRef.current++;
    const newNodeId = `${nodeId}-${type}`;
    const newPlusId = `${plusId}-plus`;

    const newNode: Node = {
      id: newNodeId,
      type: `${type}`,
      position: { x: newNodeX, y: 32 },
      data: {
        label: undefined,
        ...nodeDataDefaults(),
        ...(actionData && { actionData }),
      },
    };

    const newPlusNode: Node = {
      id: newPlusId,
      type: 'plus',
      position: { x: newNodeX + newNodeWidth + NODE_GAP, y: 52.8 },
      data: { ...nodeDataDefaults() },
    };

    if (isMiddleInsertion) {
      const nextNodeId = outgoingEdge.target;

      // Shift all nodes that are at or after the next node's x position to the right
      setNodes(prevNodes => {
        const nextNode = prevNodes.find(n => n.id === nextNodeId);
        if (!nextNode) return [...prevNodes, newNode, newPlusNode];

        const shiftThreshold = nextNode.position.x;

        return [
          ...prevNodes.map(n => {
            if (n.position.x >= shiftThreshold && n.id !== parentId) {
              return { ...n, position: { ...n.position, x: n.position.x + shiftAmount } };
            }
            return n;
          }),
          newNode,
          newPlusNode,
        ];
      });

      // Rewire edges: remove old outgoing edge, add new edges
      setEdges(eds => {
        const filtered = eds.filter(e => e.id !== outgoingEdge.id);
        return [
          ...filtered,
          {
            id: `e${parentId}-${newNodeId}`,
            source: parentId,
            sourceHandle: parentId,
            target: newNodeId,
            type: 'standard',
          },
          {
            id: `e${newNodeId}-${newPlusId}`,
            source: newNodeId,
            sourceHandle: newNodeId,
            target: newPlusId,
            type: 'standard',
          },
          {
            id: `e${newPlusId}-${nextNodeId}`,
            source: newPlusId,
            sourceHandle: newPlusId,
            target: nextNodeId,
            type: 'standard',
          },
        ];
      });
    } else {
      // Appending at the end
      setNodes(prevNodes => [...prevNodes, newNode, newPlusNode]);

      setEdges(eds => [
        ...eds,
        {
          id: `e${parentId}-${newNodeId}`,
          source: parentId,
          sourceHandle: parentId,
          target: newNodeId,
          type: 'standard',
        },
        {
          id: `e${newNodeId}-${newPlusId}`,
          source: newNodeId,
          sourceHandle: newNodeId,
          target: newPlusId,
          type: 'standard',
        },
      ]);
    }
  }

  // Function to replace a node with a different type (e.g., action -> emailBuilder)
  function onReplaceNode(nodeId: string, newType: string, actionData?: any) {
    setNodes(prevNodes =>
      prevNodes.map(node => {
        if (node.id === nodeId) {
          return {
            ...node,
            type: newType,
            data: {
              ...node.data,
              ...(actionData && { actionData }),
            },
          };
        }
        return node;
      }),
    );
  }

  // Function to remove the last node and its corresponding edge
  function removeLastNode() {
    setNodes(prevNodes => prevNodes.slice(0, -1));
    setEdges(eds => eds.slice(0, -1));
  }

  // Function to remove a specific node by ID and reconnect surrounding nodes
  function removeNode(nodeId: string) {
    // Use functional updater for edges to get current state (avoids stale closure)
    setEdges(currentEdges => {
      const edgeIntoNode = currentEdges.find(e => e.target === nodeId);
      const edgeFromNode = currentEdges.find(e => e.source === nodeId);

      // The plus node after this node
      const trailingPlusId = edgeFromNode?.target;
      const edgeFromPlus = trailingPlusId ? currentEdges.find(e => e.source === trailingPlusId) : null;

      // The plus node before this node
      const precedingPlusId = edgeIntoNode?.source;

      const nodeIdsToRemove = new Set<string>([nodeId]);
      if (trailingPlusId) nodeIdsToRemove.add(trailingPlusId);

      // Remove nodes
      setNodes(prevNodes => prevNodes.filter(n => !nodeIdsToRemove.has(n.id)));

      const filtered = currentEdges.filter(e => !nodeIdsToRemove.has(e.source) && !nodeIdsToRemove.has(e.target));

      // Reconnect: precedingPlus → nextNode (if there is a next node after the trailing plus)
      if (precedingPlusId && edgeFromPlus) {
        const nextNodeId = edgeFromPlus.target;
        return [
          ...filtered,
          {
            id: `e${precedingPlusId}-${nextNodeId}`,
            source: precedingPlusId,
            sourceHandle: precedingPlusId,
            target: nextNodeId,
            type: 'standard',
          },
        ];
      }

      return filtered;
    });
  }

  function scrollToPlusNode(plusNodePosition: { x: number; y: number }) {
    setTimeout(() => {
      const centerX = plusNodePosition.x + 16;
      const centerY = plusNodePosition.y + 16;
      setCenter(centerX, centerY, { zoom, duration: 800 });
    }, 100);
  }

  // Memoized panel options to avoid unnecessary recalculations
  const panelOptions = useMemo(() => {
    const getLargestPlusElement = () => {
      let largestElement = null;
      let largestNumber = -Infinity;

      nodes.forEach((node: any) => {
        const match = node.id.match(/^(\d+)-plus$/);
        if (match) {
          const number = parseInt(match[1], 10);
          if (number > largestNumber) {
            largestNumber = number;
            largestElement = node;
          }
        }
      });

      return largestElement;
    };

    const largestPlusElement = getLargestPlusElement();

    return { onAddNode, removeLastNode, largestPlusElement, clickedPlusNodeId: workflowsState.clickedPlusNodeId };
  }, [nodes, workflowsState.clickedPlusNodeId]);

  // Callback to handle node changes
  const onNodesChange = useCallback((changes: any) => setNodes(nds => applyNodeChanges(changes, nds)), []);

  // Callback to handle edge changes
  const onEdgesChange = useCallback((changes: any) => setEdges(eds => applyEdgeChanges(changes, eds)), []);

  // Find the plus node to position the panel next to (clicked or last)
  const getTargetPlusNode = useCallback(() => {
    if (workflowsState.clickedPlusNodeId) {
      return nodes.find(n => n.id === workflowsState.clickedPlusNodeId) || null;
    }
    return nodes.reduceRight((acc: any, node: any) => {
      if (!acc && node.type === 'plus') return node;
      return acc;
    }, null);
  }, [nodes, workflowsState.clickedPlusNodeId]);

  // Memoized calculation of the left position for the panel
  const calculateLeftPosition = useMemo(() => {
    const targetPlusNode = getTargetPlusNode();
    if (!targetPlusNode) return 810;

    const xPosition = parseFloat(targetPlusNode.position.x) || 0;
    return (xPosition + 100) * zoom + viewportX;
  }, [nodes, workflowsState.showNodesType, workflowsState.clickedPlusNodeId, viewportX, zoom]);

  // Memoized calculation of the top position for the panel
  const calculateTopPostion = useMemo(() => {
    const targetPlusNode = getTargetPlusNode();
    if (!targetPlusNode) return 115;

    const yPosition = parseFloat(targetPlusNode.position.y) || 0;
    return (yPosition + 65) * zoom + viewportY;
  }, [nodes, workflowsState.showNodesType, workflowsState.clickedPlusNodeId, viewportY, zoom]);

  // Return the state and functions needed for managing workflows
  // Rebuild the canvas from an item tree (blueprint or duplicated workflow):
  // trigger, then for every item a node followed by a plus node, false
  // branches one row below. Node configurations go to the store so the
  // save transform and the validation see the prefilled data.
  const loadFromTree = useCallback((root: WorkflowItemModel, triggerName?: string) => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    let counter = 1;
    const nextId = (type: string) => {
      const id = `${counter}-${type}`;
      counter += 1;
      return id;
    };
    const link = (source: string, target: string) =>
      newEdges.push({ id: `e${source}-${target}`, source, sourceHandle: source, target, type: 'standard' });

    const place = (item: WorkflowItemModel, parentPlusId: string | null, x: number, y: number): void => {
      const related: any = item.related_object || {};
      const nodeType = item.item_type === 'trigger' ? 'trigger' : item.item_type;
      const nodeId = item.item_type === 'trigger' ? '1-node' : nextId(nodeType);
      const data: any = { label: item.item_type, ...nodeDataDefaults() };
      if (item.item_type === 'trigger') {
        data.triggerData = {
          id: related.id,
          name: triggerName || related.name || 'Trigger',
          model: related.model || '',
        };
        store.dispatch(
          setSelectedTrigger({
            id: related.id,
            name: triggerName || related.name || 'Trigger',
            model: related.model || '',
          }),
        );
      } else if (item.item_type === 'condition') {
        const conditions = (related.condition_ids || []).map((c: any) => ({
          attribute: c.attribute,
          condition: c.condition,
          value: c.value,
          valueLabel: c.value_label || c.value,
          valueType: c.value_type || 'string',
        }));
        data.conditionData = { operator: related.operator || 'and', conditions };
        store.dispatch(
          setNodeConfiguration({
            nodeId,
            nodeType: 'condition',
            data: { operator: related.operator || 'and', conditions, subgroups: [] },
          }),
        );
      } else {
        const details = related.detail_values || {};
        const actionData: any = {
          actionType: related.action_type,
          title: related.title || '',
          returnValue: related.return_value || '',
          content: related.return_value || '',
          message: related.return_value || '',
          selectedProductId:
            related.action_type === 'product' && related.return_value ? parseInt(related.return_value, 10) : undefined,
          unit: details.unit,
          amount: details.amount,
          sectionId: details.section,
          partnerId: details.recipient_user_id,
          partnerName: details.recipient_user_name,
          recipient: details.recipient_email,
        };
        data.actionData = actionData;
        store.dispatch(setNodeConfiguration({ nodeId, nodeType: 'action', data: actionData }));
      }
      newNodes.push({ id: nodeId, type: nodeType, position: { x, y }, data });
      if (parentPlusId) link(parentPlusId, nodeId);

      const width = getNodeWidth(nodeType);
      const plusId = nextId('plus');
      newNodes.push({
        id: plusId,
        type: 'plus',
        position: { x: x + width + NODE_GAP, y: y + 20.8 },
        data: { ...nodeDataDefaults() },
      });
      link(nodeId, plusId);

      if (item.true_result) place(item.true_result, plusId, x + width + NODE_GAP + PLUS_NODE_WIDTH + NODE_GAP, y);
      if (item.false_result) place(item.false_result, nodeId, x + NODE_GAP, y + 150);
    };

    place(root, null, 100, 0);
    idCounterRef.current = counter + 1;
    setNodes(newNodes);
    setEdges(newEdges);
  }, []);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    panelOptions,
    calculateLeftPosition,
    calculateTopPostion,
    loadFromTree,
  };
};

export default useWorkflows;
